import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NewInput } from '../../PIM/common';
import useDeferredRowChange from '../useDeferredRowChange';
import { sanitizeNumericInput } from '../numericInput';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';

interface TextInputCellExtras {
  maxLength?: number;
  type?: 'text' | 'number' | 'digits';
  /** `type: 'number'` only — accept a leading `-`. Defaults to true. */
  allowNegative?: boolean;
  /** `type: 'number'` only — cap the digits after the decimal point. */
  decimals?: number;
}

type TextInputCellProps = BombiixCellRendererParams<any, string> & TextInputCellExtras;

/**
 * Editable single-line text cell backed by the PIM NewInput control.
 *
 * The input owns its display value via local state so a keystroke never waits on
 * the parent's recalc pipeline, and the value is never re-set under the caret.
 * External (recalc/server) values reflow only while the cell is not focused.
 */
function TextInputCell(params: TextInputCellProps) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, disabled, readOnly, error, maxLength, type = 'text', allowNegative, decimals } = params;
  const rowId: BombiixRowId | undefined = getRowId ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let nextValue = e.target.value;
    if (type === 'number') nextValue = sanitizeNumericInput(nextValue, { allowNegative, decimals });
    if (type === 'digits') nextValue = nextValue.replace(/[^0-9]/g, '');
    setLocal(nextValue);
    if (rowId !== undefined && field) commitChange(rowId, field, nextValue);
  };

  /**
   * Full value as a hover tooltip, on disabled cells only.
   *
   * A disabled input cannot be focused, caret-scrolled or text-selected, so a value wider than its
   * column — a generated barcode or SKU, say — is truncated with no way to reveal the rest. Editable
   * cells need no title: the user can click into them and scroll.
   *
   * Set on the wrapper as well as the input because a disabled form control does not fire mouse
   * events in every browser, which suppresses the native tooltip on the control itself.
   */
  const overflowTitle = disabled && local ? local : undefined;

  let inputMode: 'text' | 'decimal' | 'numeric' = 'text';
  if (type === 'number') inputMode = 'decimal';
  if (type === 'digits') inputMode = 'numeric';

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell" title={overflowTitle}>
        <NewInput
          className="cag-cell-input"
          type="text"
          inputMode={inputMode}
          invalid={Boolean(error)}
          value={local}
          title={overflowTitle}
          placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
          }}
          onChange={handleChange}
        />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default TextInputCell;
